import { Building, ChevronLeft, ChevronRight, LogOut } from "lucide-react";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Logo } from "@/components/ui/logo";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { CompanyCredit } from "@/domains/app/components/company-credit";
import { useNavigationItems } from "@/domains/app/hooks/use-navigation-items";
import { useAppUIStore } from "@/domains/app/stores/app-ui-store";
import { useUnrepliedReviewCount } from "@/domains/reviews/hooks/use-unreplied-review-count";
import { useLanguagePreference } from "@/hooks/use-language-preference";
import { useCurrentAbility } from "@/lib/authorization";
import {
  CompactLanguageSelector,
  FullLanguageSelector,
} from "@/lib/components/i18n/language-selector";
import { SalonSelector } from "@/lib/components/salon-selector";
import { flattenSalonsWithFranchise } from "@/lib/components/salon-selector/utils";
import { useAttentionCounts } from "@/lib/hooks/use-attention-counts";
import { useAuthStore } from "@/lib/store/auth-enhanced";
import { useSalonStore } from "@/lib/store/salon";
import { useSidebarStore } from "@/lib/store/sidebar";
import { getDisplayName } from "@/lib/utils/name-utils";

// Consistent spacing system for collapsed/expanded states
const SPACING = {
  outer: (isCollapsed: boolean) => (isCollapsed ? "p-1" : "p-2"),
  inner: (isCollapsed: boolean) => (isCollapsed ? "p-0.5" : "p-1"),
  section: (isCollapsed: boolean) => (isCollapsed ? "p-1" : "p-2"),
  headerCollapsed: "p-1",
  footerCollapsed: "p-1",
} as const;

function SidebarNav({
  onNavigate,
  unreadNotificationCount,
}: {
  onNavigate?: () => void;
  unreadNotificationCount: number;
}) {
  const ability = useCurrentAbility();
  const { getNavItems } = useNavigationItems();
  const navItems = getNavItems();
  const { total: attentionCount } = useAttentionCounts();
  const unrepliedCount = useUnrepliedReviewCount();
  const open = useSidebarStore((state) => state.open);
  const state = open ? "expanded" : "collapsed";
  const location = useLocation();

  const isActivePath = (to: string) =>
    location.pathname === to || location.pathname.startsWith(`${to}/`);

  return (
    <ul className="flex w-full min-w-0 flex-col gap-1">
      {navItems
        .filter((item) => {
          if (!item.permission) return true;
          return ability?.can(item.permission.action, item.permission.subject);
        })
        .map((item) => {
          const active = isActivePath(item.to);
          // Determine overlay count for collapsed mode
          let overlayCount: number | null = null;
          if (item.to === "/notifications" && unreadNotificationCount > 0)
            overlayCount = unreadNotificationCount;
          if (item.to === "/appointments" && attentionCount > 0) overlayCount = attentionCount;
          if (item.to === "/customer-service" && unrepliedCount > 0) overlayCount = unrepliedCount;

          return (
            <li
              key={item.to}
              className={`group/menu-item relative ${state === "collapsed" ? "h-8" : ""}`}
            >
              {state === "collapsed" ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <NavLink
                      to={item.to}
                      className="absolute inset-0 z-30 flex items-center justify-center"
                      aria-current={active ? "page" : undefined}
                    >
                      <span className="sr-only">{item.label}</span>
                    </NavLink>
                  </TooltipTrigger>
                  <TooltipContent side="right" align="center">
                    <span>{item.label}</span>
                  </TooltipContent>
                </Tooltip>
              ) : (
                <NavLink
                  to={item.to}
                  className={`flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm outline-none transition-[width,height,padding] focus-visible:ring-2 h-[2.4rem] justify-start px-2 ${active ? "bg-black text-white hover:bg-black hover:text-white" : "text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground"}`}
                  aria-current={active ? "page" : undefined}
                  onClick={onNavigate}
                >
                  <item.icon className="w-4 h-4" />
                  <span className="truncate">{item.label}</span>
                </NavLink>
              )}

              {/* click-capture overlay removed — TooltipTrigger NavLink above covers clicks in collapsed mode */}

              {/* Collapsed: absolutely center the icon at the li level so layout padding/gap can't shift it, and render compact badge next to it */}
              {state === "collapsed" && (
                <span className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
                  <span
                    className={`relative z-20 ${active ? "bg-black text-white rounded-md p-1" : ""}`}
                  >
                    <item.icon className="w-4 h-4" />

                    {overlayCount && overlayCount > 0 && (
                      <span className="absolute -right-2.5 top-1 -translate-y-1/2 min-w-4 h-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] leading-4 text-center select-none z-10">
                        {overlayCount > 99 ? "99+" : overlayCount}
                      </span>
                    )}
                  </span>
                </span>
              )}

              {/* Expanded: render standard badge content at right if provided */}
              {state === "expanded" &&
                (() => {
                  // Explicitly check counts before rendering badges
                  if (item.to === "/notifications" && unreadNotificationCount === 0) return null;
                  if (item.to === "/appointments" && attentionCount === 0) return null;
                  if (item.to === "/customer-service" && unrepliedCount === 0) return null;

                  // Only call badge function if we have non-zero counts
                  if (item.badge) {
                    const badgeContent = item.badge({
                      unreadCount: unreadNotificationCount,
                      attentionCount,
                    });

                    return badgeContent ? (
                      <div className="absolute right-1 top-[calc(0.125rem+4px)]">
                        {badgeContent}
                      </div>
                    ) : null;
                  }

                  return null;
                })()}

              {/* Collapsed badge is rendered inside the centered icon container above. */}
            </li>
          );
        })}
    </ul>
  );
}

export function LeftSidebar() {
  const { t } = useTranslation("common");
  const { t: tSettings } = useTranslation("settings");
  const open = useSidebarStore((state) => state.open);
  const setOpen = useSidebarStore((state) => state.setOpen);
  const state = open ? "expanded" : "collapsed";
  // Access stores directly instead of props
  const user = useAuthStore((state) => state.user);
  const { logout } = useAuthStore();

  const currentSalon = useSalonStore((state) => state.currentSalon);
  const franchiseAccess = useSalonStore((state) => state.franchiseAccess);
  const setCurrentSalon = useSalonStore((state) => state.setCurrentSalon);

  // Get available salons from franchise access with franchise information
  const availableSalons = flattenSalonsWithFranchise(franchiseAccess);

  const { setLeftSidebarOpen, getUnreadNotificationCount } = useAppUIStore();

  const unreadNotificationCount = getUnreadNotificationCount();

  // Create current user object with fallback name handling
  const currentUser = user
    ? {
        name: getDisplayName(
          null, // No primary display name field for users
          { firstName: user.firstName, lastName: user.lastName },
          "User"
        ),
        email: user.email,
        avatar: "/placeholder.svg?height=32&width=32",
        // Generate initials from first letter of first name and last name
        initials:
          [user.firstName?.charAt(0), user.lastName?.charAt(0)]
            .filter(Boolean)
            .join("")
            .toUpperCase() || "U",
      }
    : null;

  const handleSalonChange = (salonId: string) => {
    const salon = availableSalons.find((s) => s.id === salonId);
    if (salon) {
      setCurrentSalon(salon);
    }
  };

  const handleSalonSelectorClick = () => {
    navigate("/salon-selector");
  };

  const navigate = useNavigate();
  const { currentLanguage } = useLanguagePreference();

  // Get current salon role for display
  const getCurrentSalonRole = (): string | null => {
    if (!currentSalon) return null;
    return currentSalon.role;
  };

  const handleNavigationClick = () => {
    // Close sidebar on mobile navigation
    setLeftSidebarOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
    toast.success(t("sidebar.logoutSuccess"));
  };

  // Don't render if no user
  if (!currentUser) {
    return null;
  }

  const currentSalonName = currentSalon?.name || "";

  const isCollapsed = state === "collapsed";

  const languageKeyByCode: Record<string, string> = {
    en: "english",
    de: "german",
    vi: "vietnamese",
  };
  const languageTooltipLabel = t(`language.${languageKeyByCode[currentLanguage] ?? "english"}`);

  return (
    <div
      className={`${open ? "w-70" : "w-16"} flex-col bg-page-background flex transition-all duration-300 h-full font-medium`}
      style={{
        // Safe area + the fixed global paused-bookings banner, so it doesn't
        // cover the sidebar header.
        paddingTop: "calc(var(--safe-area-inset-top, 0px) + var(--paused-banner-height, 0px))",
      }}
    >
      <div className={`${SPACING.outer(isCollapsed)} flex-1 overflow-y-auto`}>
        <TooltipProvider delayDuration={0}>
          <Card className="h-full">
            <CardContent className={`${SPACING.inner(isCollapsed)} flex flex-col h-full`}>
              {/* Header */}
              <div className={`flex flex-col gap-2 ${isCollapsed ? SPACING.headerCollapsed : ""}`}>
                {/* User Section: expanded vs collapsed */}

                {/* Salon Selector: expanded show full, collapsed show icon */}
                <div className="">
                  {state === "expanded" ? (
                    <div className="flex justify-start items-center gap-0 relative">
                      <SalonSelector
                        currentSalon={currentSalon}
                        availableSalons={availableSalons}
                        onSalonChange={handleSalonChange}
                        onTriggerClick={handleSalonSelectorClick}
                        placeholder={t("sidebar.selectCompany")}
                        variant="combobox"
                        height="tall"
                        className="py-0 px-2 w-auto flex-1 border-0 ring-0 focus-visible:ring-0"
                      />
                    </div>
                  ) : (
                    <div className="flex justify-center">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="w-8 h-8"
                            onClick={handleSalonSelectorClick}
                            aria-label={t("sidebar.selectCompany")}
                          >
                            <Building className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="right" align="center">
                          <div className="flex flex-col">
                            <span className="text-xs opacity-70">{t("sidebar.selectCompany")}</span>
                            <span className="font-medium">{currentSalonName}</span>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  )}
                </div>
              </div>

              <div className="mx-2 w-auto bg-sidebar-border h-px mt-1" />

              {/* Content */}
              <div
                className={`flex min-h-0 flex-1 flex-col gap-2 overflow-auto ${SPACING.section(isCollapsed)}`}
              >
                {/* Navigation */}
                <SidebarNav
                  onNavigate={handleNavigationClick}
                  unreadNotificationCount={unreadNotificationCount}
                />
              </div>
              {state === "expanded" ? (
                <div>
                  <div className={`flex flex-col gap-2 ${SPACING.section(isCollapsed)} pb-0`}>
                    <div className="flex justify-center pt-3">
                      <Logo width={200} className="max-w-full" />
                    </div>
                    <CompanyCredit className="px-2" />
                    <div className="w-auto bg-sidebar-border h-px" />
                  </div>

                  <div className="flex items-center gap-3 py-4 px-3">
                    <Avatar>
                      <AvatarImage
                        src={currentUser.avatar || "/placeholder.svg"}
                        alt="User Avatar"
                      />
                      <AvatarFallback>{currentUser.initials}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-foreground truncate w-[150px]">
                        {currentUser.name}
                      </div>
                      {getCurrentSalonRole() && (
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {tSettings(
                            `profile.salonRole.roles.${getCurrentSalonRole()}`,
                            getCurrentSalonRole() || "Unknown"
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleLogout}
                        className="p-2 text-destructive hover:text-destructive/80 hover:bg-destructive/10"
                        title={t("actions.logout")}
                      >
                        <LogOut className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center py-4">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Avatar className="cursor-default mb-2">
                        <AvatarImage
                          src={currentUser.avatar || "/placeholder.svg"}
                          alt="User Avatar"
                        />
                        <AvatarFallback>{currentUser.initials}</AvatarFallback>
                      </Avatar>
                    </TooltipTrigger>
                    <TooltipContent side="right" align="center">
                      <div className="flex flex-col gap-1">
                        <span className="font-medium">{currentUser.name}</span>
                        {getCurrentSalonRole() && (
                          <span className="text-xs text-muted-foreground">
                            {tSettings(
                              `profile.salonRole.roles.${getCurrentSalonRole()}`,
                              getCurrentSalonRole() || "Unknown"
                            )}
                          </span>
                        )}
                      </div>
                    </TooltipContent>
                  </Tooltip>

                  {/* Logout on its own line in collapsed mode */}
                  <div className="">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={handleLogout}
                          className="text-destructive hover:text-destructive/80 hover:bg-destructive/10"
                          title={t("actions.logout")}
                          aria-label={t("actions.logout")}
                        >
                          <LogOut className="w-4 h-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="right" align="center">
                        <span>{t("actions.logout")}</span>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </div>
              )}

              {/* Toggle Button */}
              <div
                className={`${isCollapsed ? SPACING.footerCollapsed : "pt-1"} border-t border-sidebar-border mx-2`}
              >
                {isCollapsed ? (
                  <div className="flex flex-col items-center gap-3">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          <CompactLanguageSelector />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="right" align="center">
                        <span>{languageTooltipLabel}</span>
                      </TooltipContent>
                    </Tooltip>

                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setOpen(!open)}
                          aria-label={t("sidebar.expand", "Expand")}
                          className="w-full h-7 transition-transform duration-200"
                        >
                          <ChevronRight className="h-5 w-5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="right" align="center">
                        <span className="text-sm">{t("sidebar.expand", "Expand")}</span>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                ) : (
                  <div className="flex flex-row items-center">
                    <div className="flex-1">
                      <Button
                        variant="ghost"
                        onClick={() => setOpen(!open)}
                        aria-label={t("sidebar.collapse", "Collapse")}
                        className="h-9 justify-center py-0 gap-2 px-2 transition-transform duration-200 w-full"
                      >
                        <ChevronLeft className="h-4 w-4" />
                        <span className="text-sm">{t("sidebar.collapse", "Collapse")}</span>
                      </Button>
                    </div>

                    <div className="flex-1 flex ">
                      <FullLanguageSelector variant="ghost" className="w-full" />
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TooltipProvider>
      </div>
    </div>
  );
}
